import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InputText } from "@jobber/components/InputText";
import { Icon } from "@jobber/components/Icon";
import { Text } from "@jobber/components/Text";
import { showToast } from "@jobber/components/Toast";
import AuthFlowFrame from "components/AuthFlowFrame";
import ProXtraPromotionalPanel from "components/ProXtraPromotionalPanel";
import { useProXtraFlow } from "context";
import { formatPhoneForDisplay, stripPhoneFormatting } from "helpers";
import { sendVerificationCode, verifyCode } from "services";

const RESEND_COOLDOWN_SECONDS = 30;

function Step2() {
    const navigate = useNavigate();
    const { phoneNumber } = useProXtraFlow();

    const [codeValue, setCodeValue] = useState("");
    const [codeError, setCodeError] = useState<string | null>(null);
    const [serverError, setServerError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);

    // Redirect to Step 1 if no phone number in context
    useEffect(() => {
        if (!phoneNumber) {
            navigate("/jobber-homedepot-step1", { replace: true });
        }
    }, [phoneNumber, navigate]);

    // Countdown timer for resend cooldown
    useEffect(() => {
        if (resendCountdown <= 0) return;

        const interval = setInterval(() => {
            setResendCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [resendCountdown]);

    const handleCodeChange = (value: string | number | boolean | Date) => {
        const stringValue = String(value);
        // Only allow digits, cap at 6 characters
        const digitsOnly = stringValue.replace(/\D/g, "").slice(0, 6);
        setCodeValue(digitsOnly);
        if (codeError) setCodeError(null);
        if (serverError) setServerError(null);
    };

    const handleVerify = async () => {
        setCodeError(null);
        setServerError(null);

        // Client-side validation: must be exactly 6 digits
        if (!codeValue || codeValue.length !== 6) {
            setCodeError("Invalid code, please check and try again");
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await verifyCode(
                stripPhoneFormatting(phoneNumber),
                codeValue,
            );

            if (result.success) {
                // The backend resolves the ProXtra account and returns which
                // result screen to show: "connected", "no-account", or "in-use".
                navigate(
                    `/jobber-homedepot-step3/${result.variant ?? "no-account"}`,
                );
            } else {
                // Wrong code — Twilio returned "pending" or similar
                setCodeError("Invalid code, please check and try again");
            }
        } catch {
            setServerError(
                "There was an error connecting your account. Please try again later.",
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResend = async () => {
        if (resendCountdown > 0) return;

        try {
            await sendVerificationCode(stripPhoneFormatting(phoneNumber));
            showToast({
                message: "Verification code sent",
                variation: "success",
            });
            setResendCountdown(RESEND_COOLDOWN_SECONDS);
        } catch {
            setServerError(
                "We couldn't send the code. Check the phone number and try again.",
            );
        }
    };

    if (!phoneNumber) return null;

    const formattedPhone = formatPhoneForDisplay(phoneNumber);

    return (
        <AuthFlowFrame
            pretitle="Connect your Pro Xtra account"
            title="Enter verification code"
            description={
                <>
                    Please enter the 6 digit code sent to{" "}
                    <strong>{formattedPhone}</strong>
                </>
            }
            serverError={serverError}
            sidePanel={<ProXtraPromotionalPanel />}
            secondaryButton={{
                text: "Back",
                onClick: () => navigate("/jobber-homedepot-step1"),
            }}
            primaryButton={{
                text: "Verify Phone Number",
                onClick: handleVerify,
                disabled: isSubmitting,
                loading: isSubmitting,
            }}
        >
            <div style={{ maxWidth: "440px", width: "100%" }}>
                <InputText
                    value={codeValue}
                    onChange={handleCodeChange}
                    placeholder="Verification code"
                    invalid={!!codeError}
                    keyboard="numeric"
                />
                {codeError && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--space-smallest)",
                            marginTop: "var(--space-smaller)",
                            color: "var(--color-critical)",
                        }}
                    >
                        <Icon name="alert" size="small" color="critical" />
                        <Text size="small" variation="error">
                            {codeError}
                        </Text>
                    </div>
                )}
            </div>

            <div style={{ marginTop: "var(--space-small)" }}>
            <Text size="small">
                Didn&apos;t receive a code?{" "}
                {resendCountdown > 0 ? (
                    <span style={{ color: "var(--color-text--secondary)" }}>
                        Resend ({resendCountdown})
                    </span>
                ) : (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleResend();
                        }}
                    >
                        Resend
                    </a>
                )}
            </Text>
            </div>
        </AuthFlowFrame>
    );
}

export default Step2;
